import { useState, useEffect, CSSProperties, useMemo } from 'react'
import { Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import {
  EEnhancementImageType,
  EnhancementImageResponse
} from 'models/product'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './AiEnhancementModal.module.scss'
import { Main } from './ui/Main'
import { Photo } from './ui/ Photo'
import {
  IAiEnhancementApplyData,
  IAiEnhancementDraft,
  IAiEnhancementSettings,
  IAiProductPhoto,
  TEnhancementStep
} from './types'
import { EnhancementSettings } from './ui/EnhancementSettings'
import { EnhancementProgress } from './ui/EnhancementProgress'
import { EnhancementReview } from './ui/EnhancementReview'
import { useEnhanceProductJobs } from './hooks/useEnhanceProductJobs'
import {
  getEnhanceImageListType,
  getEnhanceImageRequestType
} from './utils/enhanceProduct'

interface Rect {
  top: number
  left: number
  right: number
  bottom: number
  width: number
  height: number
}

interface AiEnhancementModalProps {
  visible: boolean
  productId: string
  productName?: string
  onClose: () => void
  buttonCoordinates?: Rect | null
  afterModalClose?: () => void
  type: 'create' | 'edit'
  scope?: 'all' | 'image'
  currentDescription?: string
  photos?: IAiProductPhoto[]
  onApply?: (data: IAiEnhancementApplyData) => void | Promise<void>
  updateImagesList?: (type: EEnhancementImageType, images: EnhancementImageResponse[] | null, selectedImage: EnhancementImageResponse | null) => void
}

export const AiEnhancementModal: React.FC<AiEnhancementModalProps> = ({
  visible,
  productId,
  productName,
  onClose,
  buttonCoordinates,
  afterModalClose,
  type,
  scope,
  currentDescription = '',
  photos = [],
  onApply = () => { },
  updateImagesList = () => { }
}) => {
  const [isMobileView, setIsMobileView] = useState(false)

  const [improveDescription, setImproveDescription] = useState(false)
  const [characteristics, setCharacteristics] = useState(false)
  const [productImage, setProductImage] = useState(false)
  const [imageEnhancementType, setImageEnhancementType] = useState(EEnhancementImageType.FULL)
  const [improveDescriptionPrompt, setImproveDescriptionPrompt] = useState('')
  const [fullEnhancementImagePrompt, setFullEnhancementImagePrompt] = useState('')

  const [imagesWithBackground, setImagesWithBackground] = useState<EnhancementImageResponse[]>([])
  const [selectedImageWithBackground, setSelectedImageWithBackground] = useState<EnhancementImageResponse | null>(null)

  const [step, setStep] = useState(1)
  const [enhancementStep, setEnhancementStep] =
    useState<TEnhancementStep>('settings')
  const [settings, setSettings] = useState<IAiEnhancementSettings>({
    improveDescription: false,
    descriptionPrompt: '',
    productImage: false,
    imageType: EEnhancementImageType.FULL,
    imagePrompt: '',
    selectedPhoto: null
  })
  const [draft, setDraft] = useState<IAiEnhancementDraft>({})

  const {
    isRunning,
    jobStates,
    result,
    start,
    reset
  } = useEnhanceProductJobs(productId)

  const isImageEdit = useMemo(() => scope === 'image' && type === 'edit', [scope, type])
  const isFormEnhancement = type === 'create'

  const availablePhotos = useMemo(
    () => photos.filter((photo) => !photo.originFileObj),
    [photos]
  )

  const handleGenerate = () => {
    if (isImageEdit) {
      if (productImage && imageEnhancementType === EEnhancementImageType.BACKGROUND) {
        return setStep(2)
      }
      updateImagesList(imageEnhancementType, null, null)
      onClose()
    } else {
      if (!improveDescription && !characteristics && !productImage) {
        return showMessage('info', 'Выберите хотя бы один пункт', '')
      }
      if (productImage && imageEnhancementType === EEnhancementImageType.BACKGROUND) {
        return setStep(2)
      }
    }
  }

  const handleSaveStep2 = () => {
    updateImagesList(imageEnhancementType, imagesWithBackground, selectedImageWithBackground)
    onClose()
  }

  const resetProductEnhancementState = () => {
    setEnhancementStep('settings')
    setDraft({})
    reset()
  }

  useEffect(() => {
    setStep(1)
    if (!isFormEnhancement && visible) {
      resetProductEnhancementState()
    }
  }, [isFormEnhancement, visible])

  useEffect(() => {
    if (!visible || !isFormEnhancement || enhancementStep !== 'settings') {
      return
    }

    setSettings({
      improveDescription: false,
      descriptionPrompt: '',
      productImage: false,
      imageType: EEnhancementImageType.FULL,
      imagePrompt: '',
      selectedPhoto: availablePhotos[0] ?? null
    })
  }, [availablePhotos, enhancementStep, isFormEnhancement, visible])

  useEffect(() => {
    if (enhancementStep !== 'generating' || isRunning) {
      return
    }

    const nextDraft = {
      description: result.description ?? undefined,
      image: result.image ?? undefined
    }

    if (nextDraft.description || nextDraft.image) {
      setDraft(nextDraft)
      setEnhancementStep('review')
      return
    }

    const hasError =
      jobStates.description.status === 'error' ||
      jobStates.image.status === 'error'

    if (hasError && visible) {
      showMessage(
        'error',
        'Не удалось сгенерировать улучшение',
        jobStates.image.error || jobStates.description.error
      )
    }
  }, [
    enhancementStep,
    isRunning,
    jobStates.description.status,
    jobStates.description.error,
    jobStates.image.status,
    jobStates.image.error,
    result.description,
    result.image
  ])

  useEffect(() => {
    if (isImageEdit) {
      setProductImage(true)
      setImageEnhancementType(EEnhancementImageType.FULL)
    }
  }, [isImageEdit])

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 530)
    }
    checkMobileView() // Первичная проверка
    window.addEventListener('resize', checkMobileView)
    return () => window.removeEventListener('resize', checkMobileView)
  }, [])

  const modalWidth = isFormEnhancement ? 860 : 370
  let finalModalStyle: CSSProperties = {}

  if (!isFormEnhancement && !isMobileView && buttonCoordinates) {
    finalModalStyle = {
      position: 'fixed',
      top: buttonCoordinates.bottom + 20,
      left: buttonCoordinates.right - modalWidth,
      margin: 0,
      padding: 0,
      transformOrigin: 'top right'
    }
  }

  const updateSettings = (patch: Partial<IAiEnhancementSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...patch
    }))
  }

  const handleProductEnhancementGenerate = async () => {
    if (!settings.improveDescription && !settings.productImage) {
      return showMessage('info', 'Выберите хотя бы один пункт', '')
    }

    if (settings.productImage && !settings.selectedPhoto) {
      return showMessage('info', 'Выберите фото товара', '')
    }

    const imageListType = getEnhanceImageListType(settings.imageType)

    setDraft({})
    setEnhancementStep('generating')
    try {
      await start(
        {
          productId,
          ...(settings.improveDescription
            ? {
              description: {
                additionalPrompt: settings.descriptionPrompt.trim(),
                select: true
              }
            }
            : {}),
          ...(settings.productImage && settings.selectedPhoto
            ? {
              image: {
                additionalPrompt: settings.imagePrompt.trim(),
                select: true,
                type: getEnhanceImageRequestType(settings.imageType),
                photoName: settings.selectedPhoto.uid
              }
            }
            : {})
        },
        imageListType
      )
    } catch (e) {
      showServerError(e)
      setEnhancementStep('settings')
    }
  }

  const handleAccept = (data: IAiEnhancementApplyData) => {
    return onApply(data)
  }

  const handleReviewDone = () => {
    resetProductEnhancementState()
    onClose()
  }

  const handleBackToSettings = () => {
    reset()
    setDraft({})
    setEnhancementStep('settings')
  }

  return (
    <Modal
      title={isImageEdit ? 'Повторная генерация изображения' : 'AI улучшение'}
      open={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      className={`${styles.modalContainer} ${isFormEnhancement ? styles.productEnhancementModal : ''}`}
      width={modalWidth}
      style={step === 1 && !isImageEdit ? finalModalStyle : {}}
      centered={isFormEnhancement || step !== 1 || isImageEdit ? true : isMobileView}
      mask={isFormEnhancement || step !== 1 || isImageEdit ? true : isMobileView}
      getContainer={isFormEnhancement || step !== 1 || isImageEdit ? undefined : isMobileView ? undefined : false}
      afterClose={afterModalClose}
    >
      {isFormEnhancement && enhancementStep === 'settings' && (
        <EnhancementSettings
          settings={settings}
          photos={photos}
          onChange={updateSettings}
          onGenerate={handleProductEnhancementGenerate}
        />
      )}
      {isFormEnhancement && enhancementStep === 'generating' && (
        <EnhancementProgress
          items={[
            {
              title: 'Описание',
              active: settings.improveDescription,
              percent: jobStates.description.percent,
              message: jobStates.description.message,
              error: jobStates.description.error
            },
            {
              title: 'Картинка товара',
              active: settings.productImage,
              percent: jobStates.image.percent,
              message: jobStates.image.message,
              error: jobStates.image.error
            }
          ]}
          onBack={handleBackToSettings}
        />
      )}
      {isFormEnhancement && enhancementStep === 'review' && (
        <EnhancementReview
          initialDescription={currentDescription}
          selectedPhoto={settings.selectedPhoto}
          draft={draft}
          onAccept={handleAccept}
          onDone={handleReviewDone}
        />
      )}
      {!isFormEnhancement && step === 1 && (
        <Main
          improveDescription={improveDescription}
          setImproveDescription={setImproveDescription}
          improveDescriptionPrompt={improveDescriptionPrompt}
          setImproveDescriptionPrompt={setImproveDescriptionPrompt}
          characteristics={characteristics}
          setCharacteristics={setCharacteristics}
          productImage={productImage}
          setProductImage={setProductImage}
          imageEnhancementType={imageEnhancementType}
          setImageEnhancementType={setImageEnhancementType}
          fullEnhancementImagePrompt={fullEnhancementImagePrompt}
          setFullEnhancementImagePrompt={setFullEnhancementImagePrompt}
          handleGenerate={handleGenerate}
          scope={scope}
        />
      )}
      {!isFormEnhancement && (
        <Photo productId={productId} isVisible={step === 2} imagesWithBackground={imagesWithBackground} setImagesWithBackground={setImagesWithBackground} selectedImageWithBackground={selectedImageWithBackground} setSelectedImageWithBackground={setSelectedImageWithBackground} onSave={handleSaveStep2} />
      )}
    </Modal>
  )
}
